import React, { useMemo } from 'react';
import styled from 'styled-components';

export interface PaginationControlsProps {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  hideWhenSinglePage?: boolean;
  className?: string;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[2]}`};
  flex-wrap: wrap;
`;

const Summary = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray600};
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
`;

const PageSizeSelect = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  height: 32px;
  padding: 0 10px;
  background: ${({ theme }) => theme.colors.gray0};
  color: ${({ theme }) => theme.colors.gray800};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  height: 32px;
  min-width: 32px;
  padding: 0 10px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.gray300)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.gray0)};
  color: ${({ theme, $active }) => ($active ? theme.colors.gray0 : theme.colors.gray800)};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const buildVisiblePages = (currentPage: number, totalPages: number): number[] => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, 5];
  }

  if (currentPage >= totalPages - 2) {
    return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
};

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  hideWhenSinglePage = true,
  className,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(totalItems, safePage * pageSize);

  const visiblePages = useMemo(() => buildVisiblePages(safePage, totalPages), [safePage, totalPages]);

  if (hideWhenSinglePage && totalPages <= 1) {
    return null;
  }

  return (
    <Container className={className}>
      <Summary>
        Showing {start}-{end} of {totalItems}
      </Summary>

      <RightGroup>
        {onPageSizeChange && (
          <PageSizeSelect
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </PageSizeSelect>
        )}

        <PageButton onClick={() => onPageChange(safePage - 1)} disabled={safePage <= 1}>
          Prev
        </PageButton>

        {visiblePages.map((page) => (
          <PageButton key={page} $active={page === safePage} onClick={() => onPageChange(page)}>
            {page}
          </PageButton>
        ))}

        <PageButton onClick={() => onPageChange(safePage + 1)} disabled={safePage >= totalPages}>
          Next
        </PageButton>
      </RightGroup>
    </Container>
  );
};
